import React, { useMemo } from 'react';
import { Icon } from '../../icons';
import { Source, Layer, Marker } from 'react-map-gl/maplibre';
import type { Feature, FeatureCollection, Polygon } from 'geojson';
import type { HallPlan } from '../../data/hallPlans';

// Layer id for the interactive (clickable) 3D stands — imported by App for interactiveLayerIds.
export const HALL_STAND_LAYER = 'hall-stands-fill';

// Destination point given start lng/lat, bearing (deg) and distance (metres).
function dest(lng: number, lat: number, brgDeg: number, distM: number): [number, number] {
  const R = 6378137, d = distM / R, b = brgDeg * Math.PI / 180, la = lat * Math.PI / 180, lo = lng * Math.PI / 180;
  const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(b));
  const lo2 = lo + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
  return [lo2 * 180 / Math.PI, la2 * 180 / Math.PI];
}

// Initial bearing (deg) from one lng/lat point to another.
function bearing(from: [number, number], to: [number, number]): number {
  const φ1 = from[1] * Math.PI / 180, φ2 = to[1] * Math.PI / 180, Δλ = (to[0] - from[0]) * Math.PI / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2), x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

// Average of a polygon's outer-ring vertices (drops the closing duplicate point).
function centroid(feature: Feature): [number, number] {
  const ring = (feature.geometry as Polygon).coordinates[0];
  const pts = ring.slice(0, ring.length - 1);
  let lng = 0, lat = 0;
  for (const [x, y] of pts) { lng += x; lat += y; }
  return [lng / pts.length, lat / pts.length];
}

const FOV_DEG = 70;
const RANGE_M = 20;

export interface HallCamera {
  id: string;
  /** Label of the stand the camera is mounted on. */
  stand: string;
  /** Name of the sensor holding this camera's RTMP URL, if one is linked. */
  sensorName?: string;
  position: [number, number];
  /** Compass bearing the camera faces, degrees. */
  heading: number;
  fov: number;
  rangeM: number;
}

interface Props {
  plan: HallPlan;
  onSelectCamera?: (camera: HallCamera) => void;
  selectedCameraId?: string | null;
}

function HallOverlay({ plan, onSelectCamera, selectedCameraId }: Props) {
  const { fovCollection, cameras } = useMemo(() => {
    const cams: HallCamera[] = [];
    plan.cameras.forEach((def, i) => {
      const f = plan.stands.features.find(ft => ft.properties?.label === def.stand);
      if (!f) return;
      const position = centroid(f);
      cams.push({
        id: `${plan.pavilion}-cam-${i + 1}`,
        stand: def.stand,
        sensorName: def.sensorName,
        position,
        heading: bearing(position, plan.center),
        fov: FOV_DEG,
        rangeM: RANGE_M,
      });
    });

    const features: Feature[] = cams.map(cam => {
      const start = cam.heading - cam.fov / 2;
      const steps = 12;
      const ring: [number, number][] = [cam.position];
      for (let s = 0; s <= steps; s++) {
        ring.push(dest(cam.position[0], cam.position[1], start + (cam.fov * s) / steps, cam.rangeM));
      }
      ring.push(cam.position);
      return {
        type: 'Feature',
        properties: { id: cam.id, selected: cam.id === selectedCameraId },
        geometry: { type: 'Polygon', coordinates: [ring] },
      };
    });

    const collection: FeatureCollection = { type: 'FeatureCollection', features };
    return { fovCollection: collection, cameras: cams };
  }, [plan, selectedCameraId]);

  return (
    <>
      {/* Floor plan raster */}
      <Source
        id="hall-floor"
        type="image"
        url={plan.floorImage}
        coordinates={plan.floorCoords as [[number, number], [number, number], [number, number], [number, number]]}
      >
        <Layer id="hall-floor" type="raster" paint={{ 'raster-opacity': 0.9 }} />
      </Source>

      {/* Hall outline (perimeter only, not extruded) */}
      <Source id="hall-walls" type="geojson" data={plan.walls}>
        <Layer id="hall-walls-line" type="line" paint={{ 'line-color': '#94a3b8', 'line-width': 2 }} />
      </Source>

      {/* Camera FOV cones. Split across two filtered layers because
          fill-extrusion-opacity does not accept data-driven expressions. */}
      <Source id="hall-fov" type="geojson" data={fovCollection}>
        <Layer
          id="hall-fov-fill"
          type="fill-extrusion"
          filter={['!', ['get', 'selected']]}
          paint={{
            'fill-extrusion-color': '#22d3ee',
            'fill-extrusion-opacity': 0.16,
            'fill-extrusion-height': 2.6,
          }}
        />
        <Layer
          id="hall-fov-fill-selected"
          type="fill-extrusion"
          filter={['get', 'selected']}
          paint={{
            'fill-extrusion-color': '#67e8f9',
            'fill-extrusion-opacity': 0.45,
            'fill-extrusion-height': 2.6,
          }}
        />
      </Source>

      {/* Stands — the 3D extrusion */}
      <Source id="hall-stands" type="geojson" data={plan.stands}>
        <Layer
          id={HALL_STAND_LAYER}
          type="fill-extrusion"
          paint={{
            'fill-extrusion-color': ['get', 'color'],
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.82,
            'fill-extrusion-vertical-gradient': true,
          }}
        />
        <Layer
          id="hall-stands-labels"
          type="symbol"
          layout={{
            'text-field': ['get', 'label'],
            'text-size': 10,
            'symbol-placement': 'point',
          }}
          paint={{
            'text-color': '#e2e8f0',
            'text-halo-color': '#0b1020',
            'text-halo-width': 1.2,
          }}
        />
      </Source>

      {/* Camera markers — clickable, open the camera card in App */}
      {cameras.map(cam => (
        <Marker
          key={cam.id}
          longitude={cam.position[0]}
          latitude={cam.position[1]}
          anchor="center"
          onClick={(e) => {
            e.originalEvent.stopPropagation();
            onSelectCamera?.(cam);
          }}
        >
          <span
            role="button"
            tabIndex={0}
            aria-label={`Camera on stand ${cam.stand}`}
            title={`Camera — stand ${cam.stand}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectCamera?.(cam); }
            }}
            style={{
              display: 'block', cursor: 'pointer', fontSize: 16, lineHeight: 1,
              padding: 3, borderRadius: '50%',
              background: cam.id === selectedCameraId ? 'rgba(34,211,238,0.9)' : 'rgba(11,16,32,0.75)',
              border: `1px solid ${cam.id === selectedCameraId ? '#67e8f9' : '#475569'}`,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
            }}
          >
            <Icon name="camera" />
          </span>
        </Marker>
      ))}
    </>
  );
}

export default HallOverlay;
